import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import {
  FullscreenButton,
  WeeklyClipsBoard,
  useWakeLock,
  weeklyQueryOptions,
  type Location,
} from "@/pages/WeeklyClips";
import { MonitorX } from "lucide-react";

/**
 * Login-free wall display — /wall/<readable-name>-<code>. Shows the live
 * Weekly Clips board for the link's location (always in TV layout: no app
 * header, screen kept awake). No session needed; the unguessable slug is
 * the only gate (see src/server/displayLinks.ts). Deliberately does NOT
 * call useAuth, so it never redirects to the login page.
 */
export default function WallDisplay({ params }: { params: { slug: string } }) {
  const [week, setWeek] = useState<string | null>(null);
  useWakeLock(true);

  // Keep the address out of search engines if it ever leaks.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  const query = trpc.display.weeklyClips.useQuery(
    { slug: params.slug, weekStart: week ?? undefined },
    {
      ...weeklyQueryOptions(week),
      // A deleted/replaced link is final — don't hammer the server with
      // retries; a transient network blip is still retried by the next poll.
      retry: (count, err) => err.data?.code !== "NOT_FOUND" && count < 3,
    },
  );
  const data = query.data;
  const code = query.error?.data?.code;

  if (!data && (code === "NOT_FOUND" || code === "TOO_MANY_REQUESTS")) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-3 bg-slate-50 text-slate-700 p-8 text-center">
        <MonitorX className="w-12 h-12 text-slate-400" />
        <h1 className="text-2xl font-semibold">
          {code === "NOT_FOUND" ? "This display link isn't active" : "Too many attempts"}
        </h1>
        <p className="text-slate-500 max-w-md">
          {code === "NOT_FOUND"
            ? "It may have been replaced or deleted. Ask an admin for the current link (Admin Menu → Weekly Clips → Wall links)."
            : "Please wait a few minutes and reload the page."}
        </p>
      </div>
    );
  }

  return (
    <WeeklyClipsBoard
      location={(data?.location as Location | undefined) ?? null}
      data={data}
      isFetching={query.isFetching}
      hasError={!!query.error}
      onRefresh={() => void query.refetch()}
      week={week}
      onWeekChange={setWeek}
      appHeader={false}
      footer={<FullscreenButton />}
    />
  );
}
